/**
 * Code Actions Plugin
 *
 * Provides quick fixes, refactoring actions, and code lenses for AI operations.
 */
import type { editor, languages, IDisposable, IRange, CancellationToken } from 'monaco-editor';
import type { EditorPlugin } from './index';
import { agentService } from '../../services/agentService';
import type { AgentAction } from '../../services/agentService';

// Store disposables for cleanup
let disposables: IDisposable[] = [];

/**
 * Execute AI action on selected code via streaming. E.17 (2026-04-29):
 * accumulates content chunks from `agentService.streamAction` and returns
 * the joined string. Backend yields JSON `{content,error}` envelopes; the
 * service layer extracts content + throws on error. Existing callers see
 * the same `{ content: string } | null` return contract — streaming is
 * an internal optimization (UX gain pending progressive-display callers).
 */
export async function executeAIAction(
  action: string,
  code: string,
  filePath?: string,
  language?: string
): Promise<{ content: string } | null> {
  try {
    let accumulated = '';
    for await (const chunk of agentService.streamAction({
      action: action as AgentAction,
      request: `${action} the following code`,
      selection: code,
      file_path: filePath,
      language,
    })) {
      accumulated += chunk;
    }
    return { content: accumulated };
  } catch (error) {
    console.error('[Code Actions] Error:', error);
    return null;
  }
}

/**
 * Create code action provider.
 */
function createCodeActionProvider(
  _monaco: typeof import('monaco-editor')
): languages.CodeActionProvider {
  return {
    provideCodeActions(
      model: editor.ITextModel,
      range: IRange,
      context: languages.CodeActionContext,
      _token: CancellationToken
    ): languages.ProviderResult<languages.CodeActionList> {
      const selectedText = model.getValueInRange(range);

      // Only show actions if there's a selection
      if (!selectedText.trim()) {
        return { actions: [], dispose: () => {} };
      }

      const language = model.getLanguageId();
      const filePath = model.uri.path;

      const actions: languages.CodeAction[] = [
        {
          title: '✨ Refactor with AI',
          kind: 'refactor.rewrite',
          command: {
            id: 'vibe.ai.refactor',
            title: 'Refactor with AI',
            arguments: [selectedText, filePath, language, range],
          },
        },
        {
          title: '🔧 Fix with AI',
          kind: 'quickfix',
          command: {
            id: 'vibe.ai.fix',
            title: 'Fix with AI',
            arguments: [selectedText, filePath, language, range],
          },
        },
        {
          title: '📝 Document with AI',
          kind: 'source.addDocumentation',
          command: {
            id: 'vibe.ai.document',
            title: 'Document with AI',
            arguments: [selectedText, filePath, language, range],
          },
        },
        {
          title: '⚡ Optimize with AI',
          kind: 'refactor.optimize',
          command: {
            id: 'vibe.ai.optimize',
            title: 'Optimize with AI',
            arguments: [selectedText, filePath, language, range],
          },
        },
        {
          title: '🧪 Generate Tests',
          kind: 'source.generateTests',
          command: {
            id: 'vibe.ai.test',
            title: 'Generate Tests',
            arguments: [selectedText, filePath, language, range],
          },
        },
        {
          title: '💡 Explain Code',
          kind: 'source.explain',
          command: {
            id: 'vibe.ai.explain',
            title: 'Explain Code',
            arguments: [selectedText, filePath, language, range],
          },
        },
      ];

      // If there are diagnostics (errors), prioritize fix action
      if (context.markers && context.markers.length > 0) {
        const errorMessages = context.markers
          .map((m) => m.message)
          .join('; ');

        actions.unshift({
          title: `🔧 Fix: ${errorMessages.substring(0, 50)}...`,
          kind: 'quickfix',
          isPreferred: true,
          command: {
            id: 'vibe.ai.fix',
            title: 'Fix Error with AI',
            arguments: [selectedText, filePath, language, range, errorMessages],
          },
        });
      }

      return {
        actions,
        dispose: () => {},
      };
    },
  };
}

/**
 * Create code lens provider for AI hints.
 */
function createCodeLensProvider(
  _monaco: typeof import('monaco-editor')
): languages.CodeLensProvider {
  return {
    provideCodeLenses(
      model: editor.ITextModel,
      _token: CancellationToken
    ): languages.ProviderResult<languages.CodeLensList> {
      const lenses: languages.CodeLens[] = [];
      const lineCount = model.getLineCount();

      for (let i = 1; i <= lineCount; i++) {
        const lineContent = model.getLineContent(i);

        // Add lens for function definitions
        if (
          lineContent.match(
            /^\s*(export\s+)?(async\s+)?function\s+\w+|^\s*(export\s+)?const\s+\w+\s*=\s*(async\s+)?\(/
          )
        ) {
          lenses.push({
            range: {
              startLineNumber: i,
              startColumn: 1,
              endLineNumber: i,
              endColumn: 1,
            },
            command: {
              id: 'vibe.ai.explain',
              title: '💡 Explain',
              arguments: [model.getLineContent(i), model.uri.path, model.getLanguageId()],
            },
          });
        }

        // Add lens for components
        if (
          lineContent.match(
            /^\s*(export\s+)?(default\s+)?function\s+[A-Z]\w*\s*\(|^\s*(export\s+)?const\s+[A-Z]\w*\s*[:=]/
          )
        ) {
          lenses.push({
            range: {
              startLineNumber: i,
              startColumn: 1,
              endLineNumber: i,
              endColumn: 1,
            },
            command: {
              id: 'vibe.ai.document',
              title: '📝 Add Docs',
              arguments: [model.getLineContent(i), model.uri.path, model.getLanguageId()],
            },
          });
        }
      }

      return {
        lenses,
        dispose: () => {},
      };
    },

    resolveCodeLens(
      _model: editor.ITextModel,
      codeLens: languages.CodeLens,
      _token: CancellationToken
    ): languages.ProviderResult<languages.CodeLens> {
      return codeLens;
    },
  };
}

/**
 * Register editor commands for AI actions.
 */
function registerCommands(
  monaco: typeof import('monaco-editor'),
  editorInstance: editor.IStandaloneCodeEditor
): void {
  const actions: { id: string; label: string; action: string }[] = [
    { id: 'vibe.ai.refactor', label: 'Refactor with AI', action: 'refactor' },
    { id: 'vibe.ai.fix', label: 'Fix with AI', action: 'fix' },
    { id: 'vibe.ai.document', label: 'Document with AI', action: 'document' },
    { id: 'vibe.ai.optimize', label: 'Optimize with AI', action: 'optimize' },
    { id: 'vibe.ai.test', label: 'Generate Tests', action: 'test' },
    { id: 'vibe.ai.explain', label: 'Explain Code', action: 'explain' },
  ];

  for (const { id, label, action } of actions) {
    const actionDisposable = editorInstance.addAction({
      id,
      label,
      keybindings: [],
      contextMenuGroupId: 'vibe-ai',
      contextMenuOrder: 1.5,
      run: async (ed) => {
        const selection = ed.getSelection();
        if (!selection) return;

        const model = ed.getModel();
        if (!model) return;

        const selectedText = model.getValueInRange(selection);
        if (!selectedText.trim()) return;

        const filePath = model.uri.path;
        const language = model.getLanguageId();

        try {
          const result = await executeAIAction(
            action,
            selectedText,
            filePath,
            language
          );

          if (result?.content) {
            // For explain action, show in a popup or panel
            if (action === 'explain') {
              // Emit custom event for UI to handle
              window.dispatchEvent(
                new CustomEvent('vibe:ai:explanation', {
                  detail: { content: result.content, code: selectedText },
                })
              );
            } else {
              // Replace selection with result
              ed.executeEdits('vibe-ai', [
                {
                  range: selection,
                  text: result.content,
                  forceMoveMarkers: true,
                },
              ]);
            }
          }
        } catch (error) {
          console.error('[Code Actions] Error:', error);
          window.dispatchEvent(
            new CustomEvent('vibe:ai:error', {
              detail: { error: String(error), action },
            })
          );
        }
      },
    });
    disposables.push(actionDisposable);
  }
}

export const codeActionsPlugin: EditorPlugin = {
  name: 'code-actions',
  description: 'AI-powered quick fixes and refactoring',

  activate(monaco, editor) {
    // Register code action provider for common languages
    const languages = [
      'typescript',
      'javascript',
      'typescriptreact',
      'javascriptreact',
      'python',
      'html',
      'css',
    ];

    for (const lang of languages) {
      const codeActionDisposable = monaco.languages.registerCodeActionProvider(
        lang,
        createCodeActionProvider(monaco)
      );
      disposables.push(codeActionDisposable);

      const codeLensDisposable = monaco.languages.registerCodeLensProvider(
        lang,
        createCodeLensProvider(monaco)
      );
      disposables.push(codeLensDisposable);
    }

    // Register commands
    registerCommands(monaco, editor);
  },

  deactivate() {
    disposables.forEach((d) => d.dispose());
    disposables = [];
  },
};
